import { useState, useEffect } from 'react';
import { Copy, Check, ThumbsUp, ThumbsDown, RotateCcw } from 'lucide-react';
import { useCopy } from './useCopy';
import { useSetMessageFeedbackMutation } from '../../redux/services/borrower-profile';
import styles from '../../styles/chat-model.module.css';

// Action row under each AI message: copy the whole answer (raw markdown),
// thumbs up/down feedback, and regenerate. Feedback is saved server-side against
// the message id and re-hydrated from history via `initialFeedback`; regenerate
// re-asks the question that produced this answer via `onRegenerate`.
const MessageActions = ({
  content,
  messageId,
  sessionId,
  isB2C = false,
  initialFeedback = null,
  onRegenerate,
}) => {
  const { copied, copy } = useCopy();
  const [feedback, setFeedback] = useState(initialFeedback);
  const [setMessageFeedback] = useSetMessageFeedbackMutation();

  // Sync when saved feedback arrives (e.g. after a history load populates it).
  useEffect(() => {
    setFeedback(initialFeedback ?? null);
  }, [initialFeedback]);

  const canRate = Boolean(messageId && sessionId);

  const applyFeedback = async (next) => {
    if (!canRate) return;
    const value = feedback === next ? null : next; // click the active one again to clear
    const previous = feedback;
    setFeedback(value); // optimistic
    try {
      await setMessageFeedback({
        chatId: sessionId,
        messageId,
        rating: value,
        isB2C,
      }).unwrap();
    } catch {
      setFeedback(previous); // revert on failure
    }
  };

  return (
    <div className={styles.messageActions}>
      <button
        type="button"
        className={`${styles.actionButton} ${copied ? styles.actionButtonCopied : ''}`}
        onClick={() => copy(content)}
        aria-label={copied ? 'Copied' : 'Copy message'}
        title={copied ? 'Copied' : 'Copy'}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>

      {canRate && (
        <>
          <button
            type="button"
            className={`${styles.actionButton} ${feedback === 'up' ? styles.actionButtonActive : ''}`}
            onClick={() => applyFeedback('up')}
            aria-label="Good response"
            aria-pressed={feedback === 'up'}
            title="Good response"
          >
            <ThumbsUp size={15} />
          </button>
          <button
            type="button"
            className={`${styles.actionButton} ${feedback === 'down' ? styles.actionButtonActive : ''}`}
            onClick={() => applyFeedback('down')}
            aria-label="Bad response"
            aria-pressed={feedback === 'down'}
            title="Bad response"
          >
            <ThumbsDown size={15} />
          </button>
        </>
      )}

      {onRegenerate && (
        <button
          type="button"
          className={styles.actionButton}
          onClick={onRegenerate}
          aria-label="Regenerate response"
          title="Regenerate"
        >
          <RotateCcw size={15} />
        </button>
      )}
    </div>
  );
};

export default MessageActions;
